import { Component, OnInit } from '@angular/core';
import { ApiService } from 'src/app/services/api/api.service';
import { SignupService } from 'src/app/services/signup/signup.service';
import { ToastService } from 'src/app/services/toast/toast.service';
import { Router } from '@angular/router';
import { FormBuilder } from '@angular/forms';
import { AuthService } from 'src/app/services/auth/auth.service';
import { MaskPipe } from 'ngx-mask';
import { StorageService } from 'src/app/services/storage/storage.service';
import {
  BreadcrumbService,
  BreadcrumbItem,
} from 'src/app/services/breadcrumb/breadcrumb.service';
import Utils from 'src/app/helpers/utils';

@Component({
  selector: 'app-dashboard-companies',
  templateUrl: './dashboard-companies.component.html',
  styleUrls: ['./dashboard-companies.component.scss'],
})
export class DashboardCompaniesComponent implements OnInit {
  permissionObj = {
    canInactive: true,
    canFilter: true,
  };

  data: CompaniesListValues[] = [];

  filterForm = this.formBuilder.group({
    name: [null, []],
    role: [null, []],
    start_date: [
      null,
      [
        Utils.validateWhenNotEmpty(Utils.validateDateValid),
        Utils.validateWhenNotEmpty(Utils.validateMaxMinToday),
      ],
    ],
    end_date: [
      null,
      [
        Utils.validateWhenNotEmpty(Utils.validateDateValid),
        Utils.validateWhenNotEmpty(Utils.validateMaxMinToday),
      ],
    ],
    status: [null, []],
    document: [null, []],
  });

  profilesForm = this.formBuilder.group({
    profiles: [[], []],
  });

  statusOptions: SelectItem[] = [
    { label: 'Ativo', value: 'active' },
    { label: 'Inativo', value: 'inactive' },
  ];

  loading = true;
  /**
   * Padrão de Filtros:
   * - hasFilters só deve ser true APÓS aplicar os filtros (método filterData())
   * - NÃO atualizar hasFilters no valueChanges do filterForm
   * - hasFilters deve ser atualizado apenas quando:
   *   1. filterData() executar com sucesso (após a requisição)
   *   2. resetForm() for chamado (define como false)
   *   3. toggleFilterModal() quando há filtros salvos no storage
   */
  hasFilters = false;
  appliedFilters: Array<{ label: string; value: string }> = [];
  showFilterModal = false;
  roleOptions: SelectItem[] = [];
  offset: number = 0;
  count: number = 0;
  isOwner: boolean = false;

  // Propriedades para o warning modal
  showStatusModal = false;
  selectedCompany: any = null;
  isActive: boolean = false;

  // Propriedades para o modal de perfis de acesso
  showAddProfilesModal = false;
  profileOptions: SelectItem[] = [];
  loadingProfiles = false;

  // UUID do registro do usuário atual (para verificar se pode inativar/ativar)
  currentUserRegisterId: string | null = null;

  constructor(
    private formBuilder: FormBuilder,
    private api: ApiService,
    private toast: ToastService,
    public signupService: SignupService,
    private router: Router,
    private authService: AuthService,
    private maskPipe: MaskPipe,
    private storageService: StorageService,
    private breadcrumbService: BreadcrumbService
  ) {}

  ngOnInit(): void {
    if (!this.authService.verifyPermission(['de_can_view_companies'])) {
      this.toast.show(
        'error',
        'Aviso',
        'Você não tem permissão para acessar essa página.'
      );
      this.router.navigateByUrl('/app/dashboard');
    }

    this.setBreadcrumb();
    this.setPermissions();
    this.getCurrentUserRegisterId();
    this.getData();
    this.getRoles();

    this.isOwner = this.storageService.getPermissionType() === 'owner';
  }

  async getCurrentUserRegisterId() {
    try {
      const res: any = await this.api.get({
        route: 'api/v2/registration/account-switch/',
        token: true,
        params: {
          type: 'profile',
        },
      });

      // Pegar o primeiro item da lista e extrair o register.id
      if (res && res.length > 0 && res[0].register && res[0].register.id) {
        this.currentUserRegisterId = res[0].register.id;
      }
    } catch (error) {
      console.error('Erro ao buscar register ID do usuário:', error);
    }
  }

  // Verificar se a empresa pertence ao usuário atual
  isCurrentUserCompany(registerUuid: string | null | undefined): boolean {
    if (!registerUuid || !this.currentUserRegisterId) {
      return false;
    }
    return registerUuid === this.currentUserRegisterId;
  }

  async getRoles() {
    try {
      const res = await this.api.get({
        route: 'api/v2/role-list',
        token: true,
      });

      this.roleOptions = res.users_type.map((item: PermissionCompany) => ({
        label: item.description,
        value: item.id,
      }));
    } catch (error) {}
  }

  async refreshData() {
    await this.getData();
  }

  async getData(page: number = 1) {
    this.loading = true;
    try {
      const filters: any = {};

      // Construir filtros a partir do formulário (se houver valores)
      const values = this.filterForm.value;
      Object.entries(values).forEach(([key, value]: any) => {
        if (value !== null && value !== '') {
          // Verificar se o campo tem erro de validação antes de incluir
          const control = this.filterForm.get(key);
          if (control && control.valid) {
            let newValue = value;
            if (key === 'start_date' || key === 'end_date') {
              newValue = value.split('/').reverse().join('-');
            }
            if (key === 'document') {
              newValue = value.replace(/\D/g, ''); // Remove apenas números
            }
            filters[key] = newValue;
          }
        }
      });

      if (filters['start_date'] && !filters['end_date']) {
        const atualDate = new Date().toISOString().split('T')[0];
        filters['end_date'] = atualDate;
      }

      if (filters['end_date'] && !filters['start_date']) {
        filters['start_date'] = '2000-01-01';
      }

      const response: any = await this.api.get({
        route: 'api/v3/companies/',
        token: true,
        params: { page: page, ...filters },
      });

      // V3 sempre retorna paginação
      this.offset = response.showing_to || 0;
      this.count = response.count || 0;
      this.data = response.results || [];
    } catch (error) {
      console.error(error);
      this.toast.show('error', 'Erro', 'Ocorreu um erro ao carregar os dados.');
    }
    this.loading = false;
  }

  redirectToDetails(id: any) {
    this.router.navigateByUrl(`/app/permissions/companies/details/${id}`);
  }

  changePageData(page: number) {
    this.loading = true;
    this.getData(page);
  }

  resetForm() {
    this.loading = true;
    this.hasFilters = false;
    this.appliedFilters = [];
    this.filterForm.reset();
    this.getData();
  }

  formatCnpj(value: string) {
    const newValue = Utils.formatDocumentForDisplay(value);

    return newValue;
  }

  generateFilterTags(formValues: any) {
    this.appliedFilters = [];

    const fieldLabels: { [key: string]: string } = {
      name: 'Empresa',
      document: 'CNPJ',
      role: 'Papel',
      status: 'Status',
      start_date: 'Data inicial',
      end_date: 'Data final',
    };

    Object.entries(formValues).forEach(([key, value]: any) => {
      if (value !== null && value !== '' && value !== undefined) {
        let displayValue = value;

        // Tratamento especial para arrays (múltipla seleção)
        if (Array.isArray(value) && value.length > 0) {
          if (key === 'role') {
            displayValue = value
              .map((id: number) => {
                const role = this.roleOptions.find((r) => r.value === id);
                return role ? role.label : id;
              })
              .join(', ');
          } else {
            displayValue = value.join(', ');
          }
        }

        // Tratamento especial para status
        if (key === 'status') {
          const statusOption = this.statusOptions.find(
            (s) => s.value === value
          );
          displayValue = statusOption ? statusOption.label : value;
        }

        // Tratamento especial para datas (já vem formatado do formulário)
        if (key === 'start_date' || key === 'end_date') {
          displayValue = value;
        }

        // Tratamento especial para CNPJ (já vem formatado do formulário)
        if (key === 'document') {
          displayValue = value;
        }

        if (fieldLabels[key]) {
          // Combinar label e valor no formato "label: valor"
          const combinedLabel = `${fieldLabels[key]}: ${String(displayValue)}`;
          this.appliedFilters.push({
            label: combinedLabel,
            value: String(displayValue),
          });
        }
      }
    });
  }

  toggleFilterModal() {
    this.showFilterModal = !this.showFilterModal;

    if (this.showFilterModal) {
      // Se houver filtros aplicados, restaurar no formulário
      if (this.hasFilters && this.appliedFilters.length > 0) {
        // Os valores já estão no formulário, apenas garantir que as tags estão atualizadas
        this.generateFilterTags(this.filterForm.value);
      } else {
        this.hasFilters = false;
        this.appliedFilters = [];
      }
    }
  }

  async filterData() {
    // Verificar se o formulário é válido antes de processar
    if (this.filterForm.invalid) {
      // Marcar todos os campos como touched para mostrar erros
      Object.keys(this.filterForm.controls).forEach((key) => {
        this.filterForm.get(key)?.markAsTouched();
      });
      return;
    }

    this.loading = true;

    try {
      const values = this.filterForm.value;

      const filters: any = {};

      Object.entries(values).forEach(([key, value]: any) => {
        if (value !== null && value !== '') {
          // Verificar se o campo tem erro de validação antes de incluir
          const control = this.filterForm.get(key);
          if (control && control.valid) {
            let newValue = value;
            if (key === 'start_date' || key === 'end_date') {
              newValue = value.split('/').reverse().join('-');
            }
            if (key === 'document') {
              newValue = value.replace(/\D/g, ''); // Remove apenas números
            }
            filters[key] = newValue;
          }
        }
      });

      if (filters['start_date'] && !filters['end_date']) {
        const atualDate = new Date().toISOString().split('T')[0];
        filters['end_date'] = atualDate;
      }

      if (filters['end_date'] && !filters['start_date']) {
        filters['start_date'] = '2000-01-01';
      }

      const response: any = await this.api.get({
        route: 'api/v3/companies/',
        token: true,
        params: { page: 1, ...filters },
      });

      // V3 sempre retorna paginação - ATRIBUIR DADOS PRIMEIRO
      this.count = response.count || 0;
      this.offset = response.showing_to || 0;
      this.data = response.results || [];

      // ✅ IMPORTANTE: hasFilters só é atualizado após aplicar os filtros com sucesso
      this.hasFilters = Object.keys(filters).length > 0;

      // ✅ Gerar as tags dos filtros aplicados
      this.generateFilterTags(values);

      this.showFilterModal = false;
    } catch (error: any) {
      console.error(error);
      this.toast.show('error', 'Erro', 'Ocorreu um erro ao carregar os dados.');
    } finally {
      this.loading = false;
    }
  }

  setPermissions() {
    const permissions = Object.keys(this.permissionObj).map(
      (key) => `de_can_${key.split('can').join('').toLowerCase()}_companies`
    );

    permissions.forEach((permission, idx) => {
      this.permissionObj[Object.keys(this.permissionObj)[idx]] =
        this.authService.verifyPermission(permission);
    });
  }

  setBreadcrumb(): void {
    const canViewCompanies = this.authService.verifyPermission(
      'de_can_view_companies'
    );
    const breadcrumbs: BreadcrumbItem[] = [
      {
        label: 'Empresas',
        url: '/app/permissions/companies',
        clickable: false, // Página atual, não precisa ser clicável
      },
    ];
    this.breadcrumbService.setBreadcrumbs(breadcrumbs);
  }

  renderProfiles(arr: any[]) {
    if (!arr || arr.length === 0) {
      return null;
    }

    const labels = arr.map((item) => item.description);

    const displayLabels =
      labels.length > 3
        ? [...labels.slice(0, 3), `+ ${labels.length - 3} itens`]
        : labels;

    return displayLabels.join(', ');
  }

  displayDateTime(dateStr: string) {
    if (!dateStr) return { date: '-', time: '' };

    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return {
      date: `${day}/${month}/${year}`,
      time: `às ${hours}:${minutes}`,
    };
  }

  displayRole(roleStr: string) {
    if (!roleStr) return { main: '-', subtitle: '' };

    // Separar o papel principal do tipo
    const parts = roleStr.split(' - ');

    if (parts.length > 1) {
      const main = parts[0];
      const subtitle = parts[1]; // Pessoa Física, Pessoa Jurídica, Fundo de Investimento
      return { main, subtitle };
    }

    return { main: roleStr, subtitle: '' };
  }

  getStatusDisplay(status: string): string {
    if (status === 'active') return 'Ativa';
    if (status === 'inactive') return 'Inativa';
    return status || '-';
  }

  isStatusInactive(status: string): boolean {
    return status === 'inactive';
  }

  toggleCompanyStatus(id: number) {
    const findedCompany = this.data.find((company) => company.id === id);
    this.selectedCompany = findedCompany;
    this.isActive = findedCompany.status === 'active';
    this.showStatusModal = true;

    // Se for ativar, já buscar os perfis disponíveis
    if (findedCompany.status === 'inactive') {
      this.getProfileOptions();
    }
  }

  async confirmStatusChange() {
    // Se estiver ativando e não tiver perfis de acesso
    // Verifica se users_type é um array vazio ou não existe
    const hasNoProfiles =
      !this.selectedCompany.users_type ||
      !Array.isArray(this.selectedCompany.users_type) ||
      this.selectedCompany.users_type.length === 0;

    if (this.selectedCompany.status === 'inactive' && hasNoProfiles) {
      // Abrir modal para adicionar perfis
      this.showStatusModal = false;
      this.showAddProfilesModal = true;
      return;
    }

    // Se já tiver perfis ou estiver inativando, prosseguir normalmente
    try {
      const res = await this.api.patch({
        route: `api/v3/companies/${this.selectedCompany.id}/`,
        token: true,
        body: {
          status:
            this.selectedCompany.status === 'active' ? 'inactive' : 'active',
        },
      });

      this.loading = true;
      this.showStatusModal = false;
      this.getData();
    } catch (error) {
      console.error(error);
      this.toast.show(
        'error',
        'Erro',
        'Ocorreu um erro ao alterar o status da empresa.'
      );
    }
  }

  closeStatusModal() {
    this.showStatusModal = false;
    // Não limpar selectedCompany aqui, pois pode ser usado no modal de perfis
    this.isActive = false;
  }

  getModalTitle(): string {
    return this.isActive ? 'Inativar empresa' : 'Ativar empresa';
  }

  getModalMessage(): string {
    return `Deseja ${
      this.isActive ? 'inativar' : 'ativar'
    } essa empresa? Todas as permissões vinculadas a essa empresa também serão afetadas.`;
  }

  getConfirmText(): string {
    return `Sim, ${this.isActive ? 'inativar' : 'ativar'} empresa`;
  }

  async getProfileOptions() {
    this.loadingProfiles = true;
    try {
      const res = await this.api.get({
        route: 'api/v2/user-profile/',
        token: true,
        params: {
          page: 1,
          type: 'list',
          status: 'Ativo',
        },
      });

      this.profileOptions = res.results
        .filter(
          (item: any) => item.status === 'Ativo' || item.status === 'active'
        )
        .map((item: any) => ({
          label: item.name,
          value: item.id,
        }));
    } catch (error) {
      this.toast.show('error', 'Erro', 'Erro ao carregar perfis de acesso');
    } finally {
      this.loadingProfiles = false;
    }
  }

  async addProfilesToCompany() {
    if (!this.selectedCompany) {
      this.toast.show('error', 'Erro', 'Empresa não selecionada');
      this.closeAddProfilesModal();
      return;
    }

    const selectedProfiles = this.profilesForm.get('profiles')?.value || [];

    if (!selectedProfiles || selectedProfiles.length === 0) {
      this.toast.show(
        'error',
        'Aviso',
        'Selecione pelo menos um perfil de acesso'
      );
      return;
    }

    try {
      // Vincular perfis à empresa - enviar todos os perfis em um único POST
      const payload: any = {
        user_profile: selectedProfiles, // Array de IDs dos perfis
        company: this.selectedCompany.company_id || this.selectedCompany.id, // Usar company_id se disponível, senão id
        company_document: this.selectedCompany.document_number,
        company_name: this.selectedCompany.company,
        owner: this.selectedCompany.owner || false,
      };

      await this.api.post({
        route: 'api/v2/user-profile-permission/',
        token: true,
        body: payload,
      });

      // Depois de vincular, ativar a empresa
      await this.api.patch({
        route: `api/v3/companies/${this.selectedCompany.id}/`,
        token: true,
        body: {
          status: 'active',
        },
      });

      this.toast.show(
        'info',
        'Sucesso',
        'Perfis de acesso adicionados e empresa ativada com sucesso'
      );
      this.showAddProfilesModal = false;
      this.profilesForm.reset();
      this.selectedCompany = null;
      this.loading = true;
      this.getData();
    } catch (error: any) {
      console.error('Erro ao adicionar perfis:', error);
      const errorMessage =
        error?.error?.message ||
        error?.message ||
        'Ocorreu um erro ao adicionar perfis de acesso';
      this.toast.show('error', 'Erro', errorMessage);
    }
  }

  closeAddProfilesModal() {
    this.showAddProfilesModal = false;
    this.profilesForm.reset();
    this.selectedCompany = null; // Limpar apenas quando fechar o modal de perfis
  }

  /**
   * Verifica se os filtros podem ser aplicados
   * Retorna false se houver campos inválidos ou se não houver nenhum filtro preenchido
   */
  get canApplyFilters(): boolean {
    // Verifica se o formulário é válido
    if (this.filterForm.invalid) {
      return false;
    }

    // Verifica se há pelo menos um filtro preenchido
    const values = this.filterForm.value;
    const hasAnyFilter = Object.values(values).some(
      (value) => value !== null && value !== '' && value !== undefined
    );

    return hasAnyFilter;
  }
}
